import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await fetch(`${BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      // Always show success — don't leak whether email exists
      setSent(true);
    } catch {
      setError("Network error — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]" data-testid="forgot-password-page">
      <SiteHeader activeNav="" />

      <main className="flex items-center justify-center min-h-[calc(100vh-120px)] px-5 py-12">
        <div className="w-full max-w-[400px]">
          <Link
            to="/signin"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] hover:text-[var(--ink)] transition-colors mb-8"
            data-testid="forgot-back-link"
          >
            <ArrowLeft size={12} strokeWidth={2} />
            Back to sign in
          </Link>

          {sent ? (
            <div className="text-center" data-testid="forgot-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 mb-5">
                <CheckCircle2 size={28} strokeWidth={1.5} className="text-green-600" />
              </div>
              <h1 className="font-serif text-[32px] leading-[1.1] text-[var(--ink)] mb-3">Check your inbox</h1>
              <p className="font-sans text-[14.5px] leading-[1.6] text-[var(--ink-2)] mb-6">
                If <strong>{email}</strong> has an account, we've sent a reset link. Check your spam folder if it doesn't arrive within a minute.
              </p>
              <p className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)]">
                Link expires in 1 hour
              </p>
              <div className="mt-8 pt-6 border-t border-[var(--hairline)]">
                <Link to="/signin" className="font-sans text-[14px] text-[var(--crimson)] hover:underline">
                  Return to sign in &rarr;
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--muted)] mb-3">
                  Daily News Highlights
                </div>
                <h1 className="font-serif text-[34px] leading-[1.05] tracking-[-0.015em] text-[var(--ink)]">
                  Forgot your password?
                </h1>
                <p className="font-sans text-[14.5px] leading-[1.5] text-[var(--ink-2)] mt-2">
                  Enter your email and we'll send a reset link.
                </p>
              </div>

              {error && (
                <div className="mb-4 flex items-start gap-2.5 rounded-[8px] border border-[var(--crimson)]/30 bg-[var(--crimson)]/5 px-4 py-3" data-testid="forgot-error">
                  <AlertCircle size={15} strokeWidth={2} className="text-[var(--crimson)] shrink-0 mt-[1px]" />
                  <p className="font-sans text-[13px] text-[var(--crimson)] leading-[1.5]">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="forgot-form" className="space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5">
                    Email address
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className="w-full h-12 px-4 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="w-full h-12 rounded-full bg-[var(--ink)] text-[var(--paper)] font-sans text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-[var(--crimson)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending…" : "Send reset link"}
                  {!loading && <ArrowRight size={15} strokeWidth={2} />}
                </button>
              </form>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
